import React, { useState, useRef, useEffect } from 'react';
import { loader, Editor } from '@monaco-editor/react';
import FeedbackPanel from './FeedbackPanel';
import ReactMarkdown from 'react-markdown';
import './CodeEditor.css';

loader.config({
  paths: {
    vs: 'https://cdn.jsdelivr.net/npm/monaco-editor@0.40.0/min/vs',
  },
});

const CodeEditor = () => {
  const [code, setCode] = useState('// Write your code here...');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [chatMessage, setChatMessage] = useState('');
  const [showChat, setShowChat] = useState(false);

  const chatEndRef = useRef(null);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatHistory]);

  const handleSubmit = async () => {
    setLoading(true);
    setResponse('');
    setShowChat(false);
    try {
      const res = await fetch('https://codementorv2-rlki.onrender.com/api/analyze_code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });

      const data = await res.json();

      if (data.response) {
        setResponse(data.response);
        setChatHistory(data.chat_history || []);
      } else if (data.error) {
        setResponse(`Error: ${data.error}`);
      }
    } catch (error) {
      setResponse(`Error: ${error.message}`);
    }
    setLoading(false);
  };

  const handleChat = async () => {
    setLoading(true);
    try {
      const res = await fetch('https://codementorv2-rlki.onrender.com/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: chatMessage,
          chat_history: chatHistory,
        }),
      });

      const data = await res.json();

      if (data.response) {
        setChatHistory([
          ...chatHistory,
          { role: 'user', message: chatMessage },
          { role: 'chatbot', message: data.response },
        ]);
        setChatMessage('');
      } else if (data.error) {
        setResponse(`Error: ${data.error}`);
      }
    } catch (error) {
      setResponse(`Error: ${error.message}`);
    }
    setLoading(false);
  };

  return (
    <div className="app-container dark-theme">
      <header className="header">
        <h1 className="gradient-text">Code Analyzer Studio</h1>
        <p className="subheading">Write. Analyze. Understand.</p>
      </header>

      <main className="main-layout">
        {/* Editor Column */}
        <section className="editor-container">
          <div className="editor-wrapper">
            <Editor
              height="100%"
              width="100%"
              defaultLanguage="python"
              theme="vs-dark"
              value={code}
              onChange={(value) => setCode(value || '')}
              options={{
                wordWrap: 'on',
                scrollBeyondLastColumn: 0,
              }}
            />
          </div>
          <div className="submit-button-wrapper">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="analyze-button"
            >
              {loading ? <div className="spinner"></div> : 'Analyze Code'}
            </button>
          </div>
        </section>

        {/* Feedback Column */}
        <aside className="feedback-container">
          <div className="markdown">
            <FeedbackPanel feedback={response} />
          </div>
          {response && (
            <button
              className="chat-button"
              onClick={() => setShowChat(true)}
            >
              Have Questions? Chat with the Code Mentor
            </button>
          )}
        </aside>
      </main>

      {/* Chat Panel */}
      {showChat && (
        <div className="chat-sidebar">
          <button onClick={() => setShowChat(false)} className="chat-close">
            &times;
          </button>

          {/* Pushed lower with margin */}
          <h3 className="chat-header" style={{ marginTop: '3rem' }}>
            Chat with the Code Mentor
          </h3>

          <div className="chat-history">
            {chatHistory.map((chat, index) => (
              <div key={index} className={`chat-message ${chat.role}`}>
                <strong>{chat.role === 'user' ? 'You' : 'Code Mentor'}:</strong>{' '}
                <ReactMarkdown>{chat.message}</ReactMarkdown>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>

          <input
            type="text"
            className="chat-input"
            placeholder="Ask a question..."
            value={chatMessage}
            onChange={(e) => setChatMessage(e.target.value)}
          />
          <button
            onClick={handleChat}
            className="chat-send"
            disabled={loading}
          >
            {loading ? <div className="spinner"></div> : 'Send'}
          </button>
        </div>
      )}
    </div>
  );
};

export default CodeEditor;
